import React, { useEffect, useMemo, useState } from 'react'
import { Text, Heading, Sidebar, SortableTableHeader, LoadingSpinner, Button, Input, Select } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { SparklesIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import {
	ResponsiveContainer,
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	BarChart,
	Bar
} from 'recharts'
import { supabase } from '../lib/supabase'

interface AgentResponse {
	id: string
	ticket_id: number
	channel: string
	ai_acted_at: string
	ai_response: string
	ai_log: string | null
	ticket_subject: string | null
	ticket_body: string | null
	ai_subject: string | null
	ai_found_answer: boolean | null
	human_observation: string | null
	human_observation_author: string | null
	human_observation_at: string | null
	ticket_created_at: string
	status: string
}

const STATUS_OPTIONS = ['new', 'open', 'pending', 'hold', 'solved', 'closed']

const AgentesIAAgenteZendesk: React.FC = () => {
	const [data, setData] = useState<AgentResponse[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)

	// Abas principais da página
	const [activeTab, setActiveTab] = useState<'historico' | 'dashboard'>('historico')

	const [sortBy, setSortBy] = useState<'ticket_id' | 'ai_acted_at' | 'ai_response' | 'ticket_created_at' | 'status'>('ai_acted_at')
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

	const [currentPage, setCurrentPage] = useState(1)
	const [itemsPerPage] = useState(20)

	// Filtros
	const [filtroStatus, setFiltroStatus] = useState('')
	const [filtroDataInicialAtuacao, setFiltroDataInicialAtuacao] = useState('')
	const [filtroDataFinalAtuacao, setFiltroDataFinalAtuacao] = useState('')
	const [filtroTicketId, setFiltroTicketId] = useState('')
	const [filtroAssunto, setFiltroAssunto] = useState('')
	const [filtroAssuntoIA, setFiltroAssuntoIA] = useState('')
	const [filtroIaEncontrouResposta, setFiltroIaEncontrouResposta] = useState('')
	const [filtroRevisadoHumano, setFiltroRevisadoHumano] = useState('')

	// Modal de detalhe
	const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
	const [detailResponses, setDetailResponses] = useState<AgentResponse[]>([])
	const [detailLoading, setDetailLoading] = useState(false)
	const [detailError, setDetailError] = useState<string | null>(null)
	const [detailTicketId, setDetailTicketId] = useState<number | null>(null)
	const [detailTab, setDetailTab] = useState<'info' | 'respostas' | 'revisao'>('info')

	// Revisão Humana (estado local)
	const [reviewText, setReviewText] = useState('')
	const [savingReview, setSavingReview] = useState(false)
	const [saveError, setSaveError] = useState<string | null>(null)
	const [saveSuccess, setSaveSuccess] = useState<string | null>(null)

	// Navegação horizontal da tabela (aba Histórico de Respostas)
	const tableScrollRef = React.useRef<HTMLDivElement | null>(null)
	const scrollTableBy = (dx: number) => {
		if (tableScrollRef.current) {
			tableScrollRef.current.scrollBy({ left: dx, behavior: 'smooth' })
		}
	}
	const handleTableWheel = (e: React.WheelEvent<HTMLDivElement>) => {
		// Converte scroll vertical do mouse em horizontal (experiência similar à outra tela)
		if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
			e.preventDefault()
			if (tableScrollRef.current) {
				tableScrollRef.current.scrollBy({ left: e.deltaY, behavior: 'smooth' })
			}
		}
	}

	useEffect(() => {
		const loadData = async () => {
			try {
				setLoading(true)
				
				// Buscar todos os registros usando paginação
				const allData: AgentResponse[] = []
				const batchSize = 1000
				let from = 0
				let hasMore = true

				while (hasMore) {
					const { data, error } = await supabase
						.from('ai_agent_ticket_history')
						.select('id, ticket_id, channel, ai_acted_at, ai_response, ai_log, ticket_subject, ticket_body, ai_subject, ai_found_answer, human_observation, human_observation_author, human_observation_at, ticket_created_at, status')
						.order('ai_acted_at', { ascending: false })
						.range(from, from + batchSize - 1)
					
					if (error) throw error

					if (data && data.length > 0) {
						allData.push(...(data as AgentResponse[]))
						
						// Se retornou menos que o batchSize, não há mais dados
						if (data.length < batchSize) {
							hasMore = false
						} else {
							from += batchSize
						}
					} else {
						hasMore = false
					}
				}

				setData(allData)
				// Atualiza o modal se estiver aberto
				if (isDetailModalOpen && detailTicketId) {
					await openDetailModal(detailTicketId)
				}
			} catch (err: any) {
				console.error('Erro ao carregar ai_agent_ticket_history:', err)
				setError('Erro ao carregar histórico do Agente Zendesk')
			} finally {
				setLoading(false)
			}
		}
		;(loadData as any)()
	}, [])

	// Função pública para atualizar dados via botão
	const handleRefresh = async () => {
		try {
			setLoading(true)
			
			// Buscar todos os registros usando paginação
			const allData: AgentResponse[] = []
			const batchSize = 1000
			let from = 0
			let hasMore = true

			while (hasMore) {
				const { data, error } = await supabase
					.from('ai_agent_ticket_history')
					.select('id, ticket_id, channel, ai_acted_at, ai_response, ai_log, ticket_subject, ticket_body, ai_subject, ai_found_answer, human_observation, human_observation_author, human_observation_at, ticket_created_at, status')
					.order('ai_acted_at', { ascending: false })
					.range(from, from + batchSize - 1)
				
				if (error) throw error

				if (data && data.length > 0) {
					allData.push(...(data as AgentResponse[]))
					
					// Se retornou menos que o batchSize, não há mais dados
					if (data.length < batchSize) {
						hasMore = false
					} else {
						from += batchSize
					}
				} else {
					hasMore = false
				}
			}

			setData(allData)
			if (isDetailModalOpen && detailTicketId) {
				await openDetailModal(detailTicketId)
			}
		} catch (err: any) {
			console.error('Erro ao atualizar dados do Agente Zendesk:', err)
			setError('Erro ao atualizar dados do Agente Zendesk')
		} finally {
			setLoading(false)
		}
	}

	const toggleSort = (key: string) => {
		if (sortBy === key) {
			setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
		} else {
			setSortBy(key as any)
			setSortOrder('asc')
		}
	}

	const formatDate = (dateString: string) => {
		const date = new Date(dateString)
		return date.toLocaleString('pt-BR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit'
		})
	}

	const previewText = (text: string, max = 120) => {
		if (!text) return '-'
		if (text.length <= max) return text
		return text.slice(0, max) + '…'
	}

	const openDetailModal = async (ticketId: number) => {
		try {
			setDetailTicketId(ticketId)
			setDetailLoading(true)
			setDetailError(null)
			setIsDetailModalOpen(true)
			const { data, error } = await supabase
				.from('ai_agent_ticket_history')
				.select('id, ticket_id, channel, ai_acted_at, ai_response, ai_log, ticket_subject, ticket_body, ai_subject, ai_found_answer, human_observation, human_observation_author, human_observation_at, ticket_created_at, status')
				.eq('ticket_id', ticketId)
				.order('ai_acted_at', { ascending: false })
			if (error) throw error
			const responses = (data as AgentResponse[]) || []
			setDetailResponses(responses)
			setReviewText(responses[0]?.human_observation || '')
		} catch (err: any) {
			console.error('Erro ao carregar respostas do ticket:', err)
			setDetailError('Erro ao carregar respostas do ticket')
		} finally {
			setDetailLoading(false)
		}
	}

	const closeDetailModal = () => {
		setIsDetailModalOpen(false)
		setDetailResponses([])
		setDetailError(null)
		setDetailTicketId(null)
		setDetailTab('info')
	}

// Mapa de revisão humana por ticket: ticket_id -> boolean
const humanReviewedByTicket = useMemo(() => {
	const map = new Map<number, boolean>()
	for (const r of data) {
		if (!map.has(r.ticket_id)) map.set(r.ticket_id, false)
		if ((r.human_observation || '').trim().length > 0) map.set(r.ticket_id, true)
	}
	return map
}, [data])

	const filteredData = useMemo(() => {
		return data.filter((item) => {
			const matchStatus = !filtroStatus || item.status === filtroStatus

			const actedAt = new Date(item.ai_acted_at).getTime()
			const startOk = !filtroDataInicialAtuacao || actedAt >= new Date(filtroDataInicialAtuacao).getTime()
			const endOk = !filtroDataFinalAtuacao || actedAt <= new Date(filtroDataFinalAtuacao + 'T23:59:59').getTime()

			const ticketIdOk = !filtroTicketId || String(item.ticket_id) === String(filtroTicketId)
			const matchAssunto = !filtroAssunto || (item.ticket_subject || '').toLowerCase().includes(filtroAssunto.trim().toLowerCase())
			const matchAssuntoIA = !filtroAssuntoIA || (item.ai_subject || '') === filtroAssuntoIA
			const matchIaFound = !filtroIaEncontrouResposta || (
				(filtroIaEncontrouResposta === 'true' && item.ai_found_answer === true) ||
				(filtroIaEncontrouResposta === 'false' && item.ai_found_answer === false)
			)

			// Revisado Humano: por ticket (qualquer linha com observação)
			const hasHumanReview = humanReviewedByTicket.get(item.ticket_id) === true
			const matchHumanReviewed = !filtroRevisadoHumano || (
				(filtroRevisadoHumano === 'true' && hasHumanReview) ||
				(filtroRevisadoHumano === 'false' && !hasHumanReview)
			)

			return matchStatus && startOk && endOk && ticketIdOk && matchAssunto && matchAssuntoIA && matchIaFound && matchHumanReviewed
		})
	}, [data, filtroStatus, filtroDataInicialAtuacao, filtroDataFinalAtuacao, filtroTicketId, filtroAssunto, filtroAssuntoIA, filtroIaEncontrouResposta, filtroRevisadoHumano, humanReviewedByTicket])

	const sortedData = useMemo(() => {
		const copy = [...filteredData]
		return copy.sort((a, b) => {
			let aValue: any = (a as any)[sortBy]
			let bValue: any = (b as any)[sortBy]

			if (sortBy === 'ai_acted_at' || sortBy === 'ticket_created_at') {
				aValue = new Date(aValue).getTime()
				bValue = new Date(bValue).getTime()
			} else if (sortBy === 'ticket_id') {
				aValue = Number(aValue)
				bValue = Number(bValue)
			} else {
				aValue = (aValue ?? '').toString().toLowerCase()
				bValue = (bValue ?? '').toString().toLowerCase()
			}

			if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
			if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
			return 0
		})
	}, [filteredData, sortBy, sortOrder])

	useEffect(() => {
		setCurrentPage(1)
	}, [sortBy, sortOrder, filtroStatus, filtroDataInicialAtuacao, filtroDataFinalAtuacao, filtroTicketId, filtroAssunto, filtroAssuntoIA, filtroIaEncontrouResposta])

	const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1
	const startIndex = (currentPage - 1) * itemsPerPage
	const endIndex = startIndex + itemsPerPage
	const pagedData = sortedData.slice(startIndex, endIndex)

	// Indicadores (cards)
	const totalRespostas = sortedData.length
	const ticketsUnicos = useMemo(() => {
		const ids = new Set<number>()
		for (const r of filteredData) ids.add(r.ticket_id)
		return ids.size
	}, [filteredData])
	const respostasHoje = useMemo(() => {
		const now = new Date()
		const y = now.getFullYear(), m = now.getMonth(), d = now.getDate()
		return filteredData.filter((r) => {
			const t = new Date(r.ai_acted_at)
			return t.getFullYear() === y && t.getMonth() === m && t.getDate() === d
		}).length
	}, [filteredData])

	// Datasets para Dashboard
	const responsesByDayData = useMemo(() => {
		const counter = new Map<string, number>()
		for (const r of filteredData) {
			const dt = new Date(r.ai_acted_at)
			// yyyy-mm-dd
			const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
			counter.set(key, (counter.get(key) || 0) + 1)
		}
		return Array.from(counter.entries())
			.sort((a, b) => a[0].localeCompare(b[0]))
			.map(([date, count]) => ({ date, count }))
	}, [filteredData])

	const statusBarData = useMemo(() => {
		const counter = new Map<string, number>()
		for (const r of filteredData) {
			const key = (r.status || 'desconhecido').toLowerCase()
			counter.set(key, (counter.get(key) || 0) + 1)
		}
		return Array.from(counter.entries())
			.sort((a, b) => b[1] - a[1])
			.map(([status, count]) => ({ status, count }))
	}, [filteredData])

	const topSubjectsData = useMemo(() => {
		const counter = new Map<string, number>()
		for (const r of filteredData) {
			const subj = (r.ticket_subject || '').trim()
			if (!subj) continue
			counter.set(subj, (counter.get(subj) || 0) + 1)
		}
		return Array.from(counter.entries())
			.sort((a, b) => b[1] - a[1])
			.slice(0, 10)
			.map(([subject, count]) => ({ subject, count }))
	}, [filteredData])

	// Opções únicas de Assunto IA para o filtro (ordenadas)
	const aiAssuntos = useMemo(() => {
		const set = new Set<string>()
		for (const r of data) {
			const s = (r.ai_subject || '').trim()
			if (s) set.add(s)
		}
		return Array.from(set).sort((a, b) => a.localeCompare(b))
	}, [data])
	if (loading) {
		return (
			<div className="flex h-screen bg-gray-100 dark:bg-gray-900">
				<Sidebar />
				<div className="flex-1 flex items-center justify-center">
					<div className="text-center">
						<LoadingSpinner size="lg" className="mx-auto mb-4" />
						<Text size="lg" color="muted">Carregando dados...</Text>
					</div>
				</div>
			</div>
		)
	}

	if (error) {
		return (
			<div className="flex h-screen bg-gray-100 dark:bg-gray-900">
				<Sidebar />
				<div className="flex-1 flex items-center justify-center">
					<div className="text-center">
						<Text size="lg" className="text-red-600 dark:text-red-400">
							{error}
						</Text>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="flex h-screen bg-gray-100 dark:bg-gray-900">
			<Sidebar />
			<div className="flex-1 flex flex-col overflow-hidden">
				<main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
					<PermissionGuard
						moduleName="atendimentos_resposta_automatica_zendesk"
						permissionType="show"
						fallback={
							<div className="flex-1 flex items-center justify-center">
								<div className="text-center">
									<Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
										Acesso Negado
									</Text>
									<Text size="sm" color="muted">
										Você não tem permissão para acessar esta página.
									</Text>
								</div>
							</div>
						}
					>
						<div className="h-full flex flex-col">
							<div className="p-6 border-b border-gray-200 dark:border-gray-700">
								<div className="flex items-center gap-3">
									<SparklesIcon className="h-7 w-7 text-purple-600 dark:text-purple-400" />
									<div>
										<Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
											Agente Zendesk
										</Heading>
										<Text size="sm" color="muted" className="mt-1">
											Histórico de tickets respondidos automaticamente pelo agente de IA
										</Text>
									</div>
									<div className="ml-auto">
										<Button variant="ghost" onClick={handleRefresh}>Atualizar</Button>
									</div>
								</div>
							</div>

							{/* Indicadores */}
							<div className="px-6 py-4">
								<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
									<div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
										<div className="text-center">
											<Text size="3xl" className="text-white font-bold mb-2">
												{totalRespostas}
											</Text>
											<Text size="sm" className="text-white">Total de Respostas</Text>
										</div>
									</div>
									<div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
										<div className="text-center">
											<Text size="3xl" className="text-white font-bold mb-2">
												{ticketsUnicos}
											</Text>
											<Text size="sm" className="text-white">Tickets Únicos</Text>
										</div>
									</div>
									<div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
										<div className="text-center">
											<Text size="3xl" className="text-white font-bold mb-2">
												{respostasHoje}
											</Text>
											<Text size="sm" className="text-white">Respostas Hoje</Text>
										</div>
									</div>
								</div>
							</div>


							{/* Filtros */}
							<div className="px-6 py-4">
								<div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
									<div className="mb-4">
										<Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
											Filtros
										</Heading>
									</div>
									<div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
										<div className="md:col-span-2">
											<Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Status</Text>
											<Select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} className="w-full">
												<option value="">Todos</option>
												{STATUS_OPTIONS.map((s) => (
													<option key={s} value={s}>{s}</option>
												))}
											</Select>
										</div>
										<div className="md:col-span-2">
											<Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data inicial Resposta IA</Text>
											<Input type="date" value={filtroDataInicialAtuacao} onChange={(e) => setFiltroDataInicialAtuacao(e.target.value)} className="w-full" />
										</div>
										<div className="md:col-span-2">
											<Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Data final Resposta IA</Text>
											<Input type="date" value={filtroDataFinalAtuacao} onChange={(e) => setFiltroDataFinalAtuacao(e.target.value)} className="w-full" />
										</div>
										<div className="md:col-span-2">
											<Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Ticket</Text>
											<Input type="number" placeholder="Ex.: 12345" value={filtroTicketId} onChange={(e) => setFiltroTicketId(e.target.value)} className="w-full" />
										</div>
										<div className="md:col-span-2">
											<Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Assunto</Text>
											<Input type="text" placeholder="Assunto do ticket" value={filtroAssunto} onChange={(e) => setFiltroAssunto(e.target.value)} className="w-full" />
										</div>
								<div className="md:col-span-2">
									<Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Assunto IA</Text>
									<Select value={filtroAssuntoIA} onChange={(e) => setFiltroAssuntoIA(e.target.value)} className="w-full">
										<option value="">Todos</option>
										{aiAssuntos.map((s) => (
											<option key={s} value={s}>{s}</option>
										))}
									</Select>
								</div>
								<div className="md:col-span-2">
									<Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">Revisado Humano</Text>
									<Select value={filtroRevisadoHumano} onChange={(e) => setFiltroRevisadoHumano(e.target.value)} className="w-full">
										<option value="">Todos</option>
										<option value="true">Sim</option>
										<option value="false">Não</option>
									</Select>
								</div>
								<div className="md:col-span-2">
									<Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">IA encontrou resposta</Text>
									<Select value={filtroIaEncontrouResposta} onChange={(e) => setFiltroIaEncontrouResposta(e.target.value)} className="w-full">
										<option value="">Todos</option>
										<option value="true">Sim</option>
										<option value="false">Não</option>
									</Select>
								</div>
										<div className="md:col-span-2 flex justify-start">
											<Button
												variant="outline"
												size="sm"
												onClick={() => {
													setFiltroStatus('')
													setFiltroDataInicialAtuacao('')
													setFiltroDataFinalAtuacao('')
													setFiltroTicketId('')
													setFiltroAssunto('')
											setFiltroAssuntoIA('')
											setFiltroIaEncontrouResposta('')
											setFiltroRevisadoHumano('')
												}}
											>
												Limpar Filtros
											</Button>
										</div>
									</div>
								</div>
							</div>

							{/* Abas */}
							<div className="px-6">
								<div className="border-b border-gray-200 dark:border-gray-700 flex gap-2">
									<button
										onClick={() => setActiveTab('historico')}
										className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'historico' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
									>
										Histórico de Respostas
									</button>
									<button
										onClick={() => setActiveTab('dashboard')}
										className={`px-4 py-2 -mb-px border-b-2 ${activeTab === 'dashboard' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
									>
										Dashboard
									</button>
								</div>
							</div>

							{activeTab === 'historico' ? (
							<div className="flex-1 p-6 pt-0">
								<div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden h-full flex flex-col">
									<div className="px-4 py-5 sm:p-6 flex-1 flex flex-col">
										<div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
											<button
												type="button"
												onClick={() => scrollTableBy(-320)}
												className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
												aria-label="Rolar para a esquerda"
											>
												<ChevronLeftIcon className="h-4 w-4" />
											</button>
											<button
												type="button"
												onClick={() => scrollTableBy(320)}
												className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 h-8 w-8 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
												aria-label="Rolar para a direita"
											>
												<ChevronRightIcon className="h-4 w-4" />
											</button>
										</div>
										<div className="flex-1 overflow-auto">
											<div ref={tableScrollRef} className="overflow-x-auto" onWheel={handleTableWheel}>
											<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
												<thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
													<tr>
														<SortableTableHeader sortKey="ticket_id" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="px-4 py-3 text-left w-48 sticky left-0 z-20 bg-gray-50 dark:bg-gray-700">
															Ticket
														</SortableTableHeader>
									<SortableTableHeader sortKey="ai_acted_at" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="w-48 text-left">
															Data Resposta IA
														</SortableTableHeader>
														<SortableTableHeader sortKey="ticket_created_at" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="w-48 text-left">
															Entrada do Ticket
														</SortableTableHeader>
									<SortableTableHeader sortKey="status" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="w-32 text-left">
															Status
														</SortableTableHeader>
									<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
										IA Encontrou
									</th>
									<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
										Assunto IA
									</th>
														<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
															Assunto
														</th>
														<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
															Log IA
														</th>
									<SortableTableHeader sortKey="ai_response" currentSort={sortBy} sortOrder={sortOrder} onSort={toggleSort} className="text-left">
										Resposta IA
														</SortableTableHeader>
													</tr>
												</thead>
												<tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
													{pagedData.map((item) => (
														<tr
															key={item.id}
															onClick={() => openDetailModal(item.ticket_id)}
															className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
														>
															<td className="px-4 py-4 text-left w-48 sticky left-0 z-10 bg-gray-50 dark:bg-gray-700">
																<Text size="sm" className="text-gray-900 dark:text-white font-medium">
																	{item.ticket_id}
																</Text>
															</td>
															<td className="px-4 py-4 text-left">
									<Text size="sm" className="text-gray-500 dark:text-gray-400">
										{formatDate(item.ai_acted_at)}
																</Text>
															</td>
															<td className="px-4 py-4 text-left">
																<Text size="sm" className="text-gray-500 dark:text-gray-400">
																	{formatDate(item.ticket_created_at)}
																</Text>
															</td>
															<td className="px-4 py-4 text-left">
																<Text size="sm" className="text-gray-700 dark:text-gray-300">
																	{item.status}
																</Text>
															</td>
									<td className="px-4 py-4 text-left">
										<Text size="sm" className="text-gray-700 dark:text-gray-300">
											{item.ai_found_answer === true ? 'Sim' : item.ai_found_answer === false ? 'Não' : '-'}
										</Text>
									</td>
									<td className="px-4 py-4 text-left">
										<Text size="sm" className="text-gray-700 dark:text-gray-300 truncate block">
											{previewText(item.ai_subject || '')}
										</Text>
									</td>
															<td className="px-4 py-4 text-left">
																<Text size="sm" className="text-gray-700 dark:text-gray-300 truncate block">
																	{previewText(item.ticket_subject || '')}
																</Text>
															</td>
															<td className="px-4 py-4 text-left">
																<Text size="sm" className="text-gray-700 dark:text-gray-300 truncate block">
																	{previewText((item.ai_log || '').replace(/\s+/g, ' '))}
																</Text>
															</td>
															<td className="px-4 py-4 text-left">
									<Text size="sm" className="text-gray-700 dark:text-gray-300 truncate block">
										{previewText(item.ai_response)}
																</Text>
															</td>
														</tr>
													))}
												</tbody>
											</table>
											</div>

											{totalPages > 1 && (
												<div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
													<div className="flex items-center justify-between">
														<div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
															<Text size="sm">
																Mostrando {startIndex + 1} a {Math.min(endIndex, sortedData.length)} de {sortedData.length} respostas
															</Text>
														</div>
														<div className="flex items-center space-x-2">
															<button
																onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
																disabled={currentPage === 1}
																className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
															>
																Anterior
															</button>
															<div className="flex space-x-1">
																{Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
																	let pageNumber: number
																	if (totalPages <= 5) {
																		pageNumber = i + 1
																	} else if (currentPage <= 3) {
																		pageNumber = i + 1
																	} else if (currentPage >= totalPages - 2) {
																		pageNumber = totalPages - 4 + i
																	} else {
																		pageNumber = currentPage - 2 + i
																	}
																	return (
																		<button
																			key={pageNumber}
																			onClick={() => setCurrentPage(pageNumber)}
																			className={`px-3 py-2 text-sm font-medium rounded-md ${
																				currentPage === pageNumber
																					? 'bg-blue-600 text-white'
																					: 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
																			}`}
																		>
																			{pageNumber}
																		</button>
																	)
																})}
															</div>
															<button
																onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
																disabled={currentPage === totalPages}
																className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
															>
																Próximo
															</button>
														</div>
													</div>
												</div>
											)}
										</div>
									</div>
								</div>
							</div>
							) : (
								<div className="px-6 py-4">
									<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
										{/* Respostas por Dia */}
										<div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
											<Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-3">
												Respostas por dia
											</Heading>
											<div className="h-64">
												<ResponsiveContainer width="100%" height="100%">
													<LineChart data={responsesByDayData}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis dataKey="date" />
														<YAxis allowDecimals={false} />
														<Tooltip />
														<Legend />
														<Line type="monotone" dataKey="count" name="Respostas" stroke="#3b82f6" strokeWidth={2} dot={false} />
													</LineChart>
												</ResponsiveContainer>
											</div>
										</div>
										{/* Distribuição por Status */}
										<div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
											<Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-3">
												Distribuição por status
											</Heading>
											<div className="h-64">
												<ResponsiveContainer width="100%" height="100%">
													<BarChart data={statusBarData}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis dataKey="status" />
														<YAxis allowDecimals={false} />
														<Tooltip />
														<Legend />
														<Bar dataKey="count" name="Respostas" fill="#10b981" />
													</BarChart>
												</ResponsiveContainer>
											</div>
										</div>
										{/* Top Assuntos */}
										<div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 lg:col-span-2">
											<Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-3">
												Top assuntos
											</Heading>
											<div className="h-80">
												<ResponsiveContainer width="100%" height="100%">
													<BarChart data={topSubjectsData} layout="vertical" margin={{ left: 24 }}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis type="number" allowDecimals={false} />
														<YAxis type="category" dataKey="subject" width={200} />
														<Tooltip />
														<Legend />
														<Bar dataKey="count" name="Ocorrências" fill="#6366f1" />
													</BarChart>
												</ResponsiveContainer>
											</div>
										</div>
									</div>
								</div>
							)}
							{/* Modal de Detalhe */}
							{isDetailModalOpen && (
								<div className="fixed inset-0 z-50 overflow-y-auto">
									<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
										<div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailModal}></div>
										<div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:p-6 max-h-[90vh] flex flex-col">
											<div className="flex items-center justify-between mb-4">
												<div>
													<Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
														Respostas do Ticket #{detailTicketId}
													</Heading>
													<Text size="sm" color="muted" className="mt-1">
														Ticket: {detailTicketId}
													</Text>
												</div>
												<button onClick={closeDetailModal} className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none">
													<XMarkIcon className="h-6 w-6" />
												</button>
											</div>
											{/* Abas do Modal */}
											<div className="border-b border-gray-200 dark:border-gray-700 mb-4">
												<nav className="-mb-px flex space-x-4" aria-label="Tabs">
													<button
														onClick={() => setDetailTab('info')}
														className={`whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm ${detailTab === 'info' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'}`}
													>
														Informações
													</button>
													<button
														onClick={() => setDetailTab('respostas')}
														className={`whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm ${detailTab === 'respostas' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'}`}
													>
														Respostas
													</button>
										<button
											onClick={() => setDetailTab('revisao')}
											className={`whitespace-nowrap py-2 px-3 border-b-2 font-medium text-sm ${detailTab === 'revisao' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'}`}
										>
											Revisão Humana
										</button>
												</nav>
											</div>

											{detailTab === 'info' ? (
												<div className="flex-1 overflow-auto">
													<table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
														<thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
															<tr>
																<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
																	Campo
																</th>
																<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
																	Valor
																</th>
															</tr>
														</thead>
														<tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
															<tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
																<td className="px-6 py-4 whitespace-nowrap text-left">
																	<Text size="sm" className="text-gray-900 dark:text-white font-medium">Ticket</Text>
																</td>
																<td className="px-6 py-4 text-left">
																	<Text size="sm" className="text-gray-700 dark:text-gray-300">{detailTicketId}</Text>
																</td>
															</tr>
															<tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
																<td className="px-6 py-4 whitespace-nowrap text-left">
																	<Text size="sm" className="text-gray-900 dark:text-white font-medium">Assunto</Text>
																</td>
																<td className="px-6 py-4 text-left">
																	<Text size="sm" className="text-gray-700 dark:text-gray-300">
																		{detailResponses.find((r) => r.ticket_subject)?.ticket_subject || '-'}
																	</Text>
																</td>
															</tr>
									<tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
										<td className="px-6 py-4 whitespace-nowrap text-left">
											<Text size="sm" className="text-gray-900 dark:text-white font-medium">Assunto IA</Text>
										</td>
										<td className="px-6 py-4 text-left">
											<Text size="sm" className="text-gray-700 dark:text-gray-300">
												{detailResponses.find((r) => r.ai_subject)?.ai_subject || '-'}
											</Text>
										</td>
									</tr>
									<tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
										<td className="px-6 py-4 whitespace-nowrap text-left">
											<Text size="sm" className="text-gray-900 dark:text-white font-medium">IA encontrou resposta</Text>
										</td>
										<td className="px-6 py-4 text-left">
											<Text size="sm" className="text-gray-700 dark:text-gray-300">
												{(() => {
													const v = detailResponses.find((r) => r.ai_found_answer !== null && r.ai_found_answer !== undefined)?.ai_found_answer
													return v === true ? 'Sim' : v === false ? 'Não' : '-'
												})()}
												</Text>
											</td>
										</tr>
															<tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
																<td className="px-6 py-4 whitespace-nowrap text-left align-top">
																	<Text size="sm" className="text-gray-900 dark:text-white font-medium">Log da IA</Text>
																</td>
																<td className="px-6 py-4 text-left">
																	<div className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
																		{detailResponses.length > 0 && detailResponses[0].ai_log
																			? detailResponses[0].ai_log
																			: '-'}
																	</div>
																</td>
															</tr>
															<tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
																<td className="px-6 py-4 whitespace-nowrap text-left">
																	<Text size="sm" className="text-gray-900 dark:text-white font-medium">Canal</Text>
																</td>
																<td className="px-6 py-4 text-left">
																	<Text size="sm" className="text-gray-700 dark:text-gray-300">{detailResponses[0]?.channel || '-'}</Text>
																</td>
															</tr>
															<tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
																<td className="px-6 py-4 whitespace-nowrap text-left">
																	<Text size="sm" className="text-gray-900 dark:text-white font-medium">Data de Criação</Text>
																</td>
																<td className="px-6 py-4 text-left">
																	<Text size="sm" className="text-gray-700 dark:text-gray-300">
																		{detailResponses.length > 0 ? formatDate(detailResponses[detailResponses.length - 1].ticket_created_at) : '-'}
																	</Text>
																</td>
															</tr>
															<tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
																<td className="px-6 py-4 whitespace-nowrap text-left">
																	<Text size="sm" className="text-gray-900 dark:text-white font-medium">Última Data Resposta IA</Text>
																</td>
																<td className="px-6 py-4 text-left">
										<Text size="sm" className="text-gray-700 dark:text-gray-300">
											{detailResponses.length > 0 ? formatDate(detailResponses[0].ai_acted_at) : '-'}
																	</Text>
																</td>
															</tr>
															<tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
																<td className="px-6 py-4 whitespace-nowrap text-left">
																	<Text size="sm" className="text-gray-900 dark:text-white font-medium">Status</Text>
																</td>
																<td className="px-6 py-4 text-left">
																	<Text size="sm" className="text-gray-700 dark:text-gray-300">{detailResponses[0]?.status || '-'}</Text>
																</td>
															</tr>
															<tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
																<td className="px-6 py-4 whitespace-nowrap text-left">
																	<Text size="sm" className="text-gray-900 dark:text-white font-medium">Total de Respostas</Text>
																</td>
																<td className="px-6 py-4 text-left">
																	<Text size="sm" className="text-gray-700 dark:text-gray-300">{detailResponses.length}</Text>
																</td>
															</tr>
														</tbody>
													</table>
												</div>
								) : detailTab === 'revisao' ? (
										<div className="flex-1 overflow-auto space-y-4">
											{false && (
												<>
										{/* Card E-mail */}
										<div className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
											<div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
												<div className="flex items-center justify-between">
													<div className="flex items-center gap-2">
														<div className="w-3 h-3 bg-red-500 rounded-full"></div>
														<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
														<div className="w-3 h-3 bg-green-500 rounded-full"></div>
														<Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium ml-2">Dados do E-mail</Text>
													</div>
													<div className="text-xs text-gray-500 dark:text-gray-500">METADADOS</div>
												</div>
											</div>
											<div className="p-6">
												<div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
													<div>
														<div className="text-gray-500 dark:text-gray-400">Data do ticket</div>
														<div className="text-gray-900 dark:text-gray-100 font-medium">{detailResponses.length > 0 ? formatDate(detailResponses[detailResponses.length - 1].ticket_created_at) : '-'}</div>
													</div>
													<div>
														<div className="text-gray-500 dark:text-gray-400">Status</div>
														<div className="text-gray-900 dark:text-gray-100 font-medium">{detailResponses[0]?.status || '-'}</div>
													</div>
													<div className="md:col-span-2">
														<div className="text-gray-500 dark:text-gray-400">Assunto</div>
														<div className="text-gray-900 dark:text-gray-100 font-medium">{detailResponses.find((r) => r.ticket_subject)?.ticket_subject || '-'}</div>
													</div>
													{detailResponses[0]?.ticket_body && (
														<div className="md:col-span-2 mt-1">
															<Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium mb-2">Corpo</Text>
															<div className="text-sm text-gray-900 dark:text-gray-100 overflow-auto max-h-96 whitespace-pre-wrap break-words leading-loose bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">{detailResponses[0]?.ticket_body}</div>
														</div>
													)}
												</div>
											</div>
										</div>

										{/* Card Resposta SilvIA */}
										<div className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
											<div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
												<div className="flex items-center justify-between">
													<div className="flex items-center gap-2">
														<div className="w-3 h-3 bg-red-500 rounded-full"></div>
														<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
														<div className="w-3 h-3 bg-green-500 rounded-full"></div>
														<Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium ml-2">Análise da IA</Text>
													</div>
													<div className="text-xs text-gray-500 dark:text-gray-500">IA</div>
												</div>
											</div>
											<div className="p-6">
												<div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mb-4">
													{detailResponses[0]?.ai_subject && (
														<div>
															<div className="text-gray-500 dark:text-gray-400">Assunto identificado</div>
															<div className="text-gray-900 dark:text-gray-100 font-medium">{detailResponses[0]?.ai_subject}</div>
														</div>
													)}
													<div>
														<div className="text-gray-500 dark:text-gray-400">IA encontrou resposta</div>
														<div>
															<span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${detailResponses[0]?.ai_found_answer === true ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : detailResponses[0]?.ai_found_answer === false ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'}`}>
																{detailResponses[0]?.ai_found_answer === true ? 'Sim' : detailResponses[0]?.ai_found_answer === false ? 'Não' : 'Indefinido'}
															</span>
														</div>
													</div>
													<div>
														<div className="text-gray-500 dark:text-gray-400">Data da análise</div>
														<div className="text-gray-900 dark:text-gray-100 font-medium">{detailResponses[0] ? formatDate(detailResponses[0].ai_acted_at) : '-'}</div>
													</div>
												</div>

												<Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium">Resposta IA</Text>
												<div className="mt-2 text-sm text-gray-900 dark:text-gray-100 overflow-auto max-h-96 leading-relaxed bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
													<div className="prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: detailResponses[0]?.ai_response || '-' }} />
												</div>
											</div>
											</div>
											</>
											)}

											{/* Card Observação Humana */}
										<div className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
											<div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
												<div className="flex items-center justify-between">
													<Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium">Observação Humana</Text>
													<div className="text-xs text-gray-500 dark:text-gray-500">REVISÃO</div>
												</div>
											</div>
											<div className="p-6 space-y-3">
												<div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
													<div>
														<div className="text-gray-500 dark:text-gray-400">Última revisão por</div>
														<div className="text-gray-900 dark:text-gray-100 font-medium">{detailResponses[0]?.human_observation_author || '-'}</div>
													</div>
													<div>
														<div className="text-gray-500 dark:text-gray-400">Data da revisão</div>
														<div className="text-gray-900 dark:text-gray-100 font-medium">{detailResponses[0]?.human_observation_at ? formatDate(detailResponses[0]!.human_observation_at as string) : '-'}</div>
													</div>
												</div>

												<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Observação</label>
												<textarea
													className="w-full mt-1 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
													rows={6}
													value={reviewText}
													onChange={(e) => { setReviewText(e.target.value); setSaveError(null); setSaveSuccess(null); }}
												/>

												<div className="flex items-center gap-3 pt-2">
													<Button
														onClick={async () => {
															try {
																setSavingReview(true)
																setSaveError(null)
																setSaveSuccess(null)
																const latest = detailResponses[0]
																if (!latest) throw new Error('Nenhuma resposta para salvar')
																const { data: authData } = await supabase.auth.getUser()
																const author = (authData?.user?.email || authData?.user?.id || '').toString()
																const nowIso = new Date().toISOString()
																const { error: upErr } = await supabase
																	.from('ai_agent_ticket_history')
																	.update({ human_observation: (reviewText || '').trim() || null, human_observation_author: author, human_observation_at: nowIso })
																	.eq('id', latest.id)
																if (upErr) throw upErr
																setSaveSuccess('Observação salva.')
																await openDetailModal(latest.ticket_id)
																setDetailTab('revisao')
															} catch (e: any) {
															setSaveError(e?.message || 'Erro ao salvar')
															} finally {
															setSavingReview(false)
															}
														}}
														disabled={savingReview}
														className="text-white"
													>
														{savingReview ? 'Salvando...' : 'Salvar'}
													</Button>
													<Button variant="ghost" onClick={() => { setReviewText(detailResponses[0]?.human_observation || ''); setSaveError(null); setSaveSuccess(null); }} disabled={savingReview}>Cancelar alterações</Button>
													{saveError && <span className="text-sm text-red-600 dark:text-red-400">{saveError}</span>}
													{saveSuccess && <span className="text-sm text-green-600 dark:text-green-400">{saveSuccess}</span>}
												</div>
											</div>
										</div>
									</div>
								) : (
									<div className="flex-1 overflow-auto space-y-4">
													{detailLoading && (
														<div className="flex items-center justify-center py-10">
															<LoadingSpinner size="lg" className="mx-auto" />
														</div>
													)}
													{detailError && !detailLoading && (
														<div className="text-center text-red-600 dark:text-red-400 py-4">{detailError}</div>
													)}
									{!detailLoading && !detailError && detailResponses.map((item) => (
										<div key={item.id} className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 border border-gray-100 dark:border-gray-700">
											{/* Seção E-mail */}
											<div className="mb-4">
												<div className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
													<div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
														<div className="flex items-center justify-between">
															<div className="flex items-center gap-2">
																<div className="w-3 h-3 bg-red-500 rounded-full"></div>
																<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
																<div className="w-3 h-3 bg-green-500 rounded-full"></div>
																<Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium ml-2">Dados do E-mail</Text>
															</div>
															<div className="text-xs text-gray-500 dark:text-gray-500">METADADOS</div>
														</div>
													</div>
													<div className="p-6">
														<div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
															<div>
																<div className="text-gray-500 dark:text-gray-400">Data do ticket</div>
																<div className="text-gray-900 dark:text-gray-100 font-medium">{formatDate(item.ticket_created_at)}</div>
															</div>
															<div>
																<div className="text-gray-500 dark:text-gray-400">Status</div>
																<div className="text-gray-900 dark:text-gray-100 font-medium">{item.status}</div>
															</div>
															{item.ticket_subject && (
																<div className="md:col-span-2">
																	<div className="text-gray-500 dark:text-gray-400">Assunto</div>
																	<div className="text-gray-900 dark:text-gray-100 font-medium">{item.ticket_subject}</div>
																</div>
															)}
														</div>
														{item.ticket_body && (
															<div className="mt-4">
																<Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium mb-2">Corpo</Text>
																<div className="text-sm text-gray-900 dark:text-gray-100 overflow-auto max-h-96 whitespace-pre-wrap break-words leading-loose bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
																	{item.ticket_body}
																</div>
															</div>
														)}
													</div>
												</div>
											</div>

											{/* Seção Resposta SilvIA */}
											<div>
												<div className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
													<div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
														<div className="flex items-center justify-between">
															<div className="flex items-center gap-2">
																<div className="w-3 h-3 bg-red-500 rounded-full"></div>
																<div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
																<div className="w-3 h-3 bg-green-500 rounded-full"></div>
																<Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium ml-2">Resposta SilvIA</Text>
															</div>
															<div className="text-xs text-gray-500 dark:text-gray-500">IA</div>
														</div>
													</div>
													<div className="p-6">
														<div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mb-4">
															{item.ai_subject && (
																<div>
																	<div className="text-gray-500 dark:text-gray-400">Assunto identificado</div>
																	<div className="text-gray-900 dark:text-gray-100 font-medium">{item.ai_subject}</div>
																</div>
															)}
															<div>
																<div className="text-gray-500 dark:text-gray-400">IA encontrou resposta</div>
																<div>
																	<span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.ai_found_answer === true ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : item.ai_found_answer === false ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'}`}>
																		{item.ai_found_answer === true ? 'Sim' : item.ai_found_answer === false ? 'Não' : 'Indefinido'}
																	</span>
																</div>
															</div>
															<div>
																<div className="text-gray-500 dark:text-gray-400">Data da análise</div>
																<div className="text-gray-900 dark:text-gray-100 font-medium">{formatDate(item.ai_acted_at)}</div>
															</div>
														</div>

														<Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium">Resposta IA</Text>
														<div className="mt-2 text-sm text-gray-900 dark:text-gray-100 overflow-auto max-h-96 leading-relaxed bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
															<div className="prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: item.ai_response || '-' }} />
														</div>
													</div>
												</div>
											</div>
										</div>
									))}
													{!detailLoading && !detailError && detailResponses.length === 0 && (
														<div className="text-center text-gray-500 dark:text-gray-400">Nenhuma resposta encontrada para este ticket.</div>
													)}
												</div>
											)}
										</div>
									</div>
								</div>
							)}
						</div>
					</PermissionGuard>
				</main>
			</div>
		</div>
	)
}

export default AgentesIAAgenteZendesk


